/**
 * How the question box drives the context selector from the keyboard.
 *
 * The selector never takes focus — the caret stays in the question while an
 * `@`-mention is being written — so the box handles the arrow keys itself and
 * the selector only reports what is on screen. Each tab publishes its rows as a
 * {@link QuestionContextList}, and the selector gathers those into a
 * {@link QuestionContextNavigator} covering whichever tab is showing.
 */

/** The rows one tab is showing, and what taking one of them does. */
export type QuestionContextList = {
	count: () => number;
	/** Takes the row at `index`, and does nothing when there is no such row. */
	select: (index: number) => void;
};

/** What a tab needs to be navigable from the question box. */
export type QuestionContextListProps = {
	/**
	 * Row the arrow keys have reached, or -1 for none. Always -1 for a tab that
	 * is not the one on screen.
	 */
	highlightedIndex: number;
	/**
	 * Publishes the tab's rows to the selector. Called once, during setup: the
	 * accessors it carries stay live as the tab's contents change.
	 */
	registerList: (list: QuestionContextList) => void;
};

/** The open selector, as the question box's key handler sees it. */
export type QuestionContextNavigator = {
	/** Rows in the tab on screen. */
	count: () => number;
	/** Takes the row at `index` in the tab on screen. */
	select: (index: number) => void;
	/** Moves `delta` tabs along the strip, wrapping at both ends. */
	stepTab: (delta: number) => void;
};

/**
 * The row to highlight on a tab.
 *
 * A tab starts on its first row, including while its list is still empty: an
 * empty list must not clear that choice, or the first row arrives with nothing
 * highlighted. A row already chosen on the tab is kept, and shortened to the
 * last row when the list no longer reaches it.
 */
export function highlightIndexForTab(
	remembered: number | undefined,
	rows: number,
): number {
	const index = remembered == null || remembered < 0 ? 0 : remembered;
	if (rows <= 0) return index;
	return Math.min(index, rows - 1);
}

/** Rows in Sources / Videos / Answers that can take keyboard focus. */
export const CONTEXT_LIST_ROW_SELECTOR = [
	".gvtui-question-context-channels__item",
	".gvtui-question-context-videos__item",
	".gvtui-question-context-answers__item",
].join(", ");

export function focusHighlightedContextRow(
	popover: HTMLElement | null | undefined,
): void {
	if (!popover) {
		return;
	}
	const row = popover.querySelector<HTMLElement>(
		[
			".gvtui-question-context-channels__item--highlighted",
			".gvtui-question-context-videos__item--highlighted",
			".gvtui-question-context-answers__item--highlighted",
		].join(", "),
	);
	row?.focus({ preventScroll: true });
}

type ContextNavigatorKeyOptions = {
	/** @default true */
	selectOnEnter?: boolean;
};

/**
 * Arrow keys (and optionally Enter) for the tab on screen. Used from the
 * question field while typing a mention, and from a focused list row after Tab.
 */
export function handleContextNavigatorKey(
	navigator: QuestionContextNavigator,
	index: number,
	event: Pick<
		KeyboardEvent,
		"key" | "shiftKey" | "metaKey" | "altKey" | "ctrlKey"
	>,
	setIndex: (index: number) => void,
	options?: ContextNavigatorKeyOptions,
): boolean {
	if (event.shiftKey || event.metaKey || event.altKey || event.ctrlKey) {
		return false;
	}

	const rows = navigator.count();
	switch (event.key) {
		case "ArrowDown": {
			if (rows <= 0) {
				return false;
			}
			setIndex(Math.min(index + 1, rows - 1));
			return true;
		}
		case "ArrowUp": {
			if (rows <= 0) {
				return false;
			}
			setIndex(Math.max(index - 1, 0));
			return true;
		}
		case "ArrowLeft":
			navigator.stepTab(-1);
			return true;
		case "ArrowRight":
			navigator.stepTab(1);
			return true;
		case "Enter":
			if (options?.selectOnEnter === false) {
				return false;
			}
			if (index < 0 || index >= rows) {
				return false;
			}
			navigator.select(index);
			return true;
		default:
			return false;
	}
}
